import React, { FC, useState, useEffect, useCallback } from 'react'
import DocumentTitle from 'react-document-title'
import { useHistory, useParams } from 'react-router-dom'
import { useDeepCompareEffect } from 'react-use'
import { Button } from 'transfix-ui/components'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { IShipperRatesSortByEnum } from 'types/graphqlTypes'
import useFilters, { SortOrder } from 'apps/marketplace/utils/hooks/useFilters'
import { Pagination } from 'transfix-ui'
import { generatePageTitle } from 'utils/generateAppDefaults'
import {
  initialQueryParams,
  ShipperRateObjectAugmented,
} from '../../utils/hooks/AwardedLanes/useAwardedLanesApi'
import AwardedLaneModal from './components/AwardedLaneModal'
import AwardedLanesFilters from './components/AwardedLanesFilters'
import {
  AwardedLanesTable,
  AwardedLanesTableRow,
  AwardedLanesEmpty,
} from './components/AwardedLanesTable/AwardedLanesTable'
import { PresourceModal } from './components/PresourceModal'
import { ReassignModal } from './components/ReassignModal'
import { useAwardedLanes } from './hooks/useAwardedLanes'

export const AwardedLanes: FC = () => {
  const [reassignModal, setReassignModal] = useState(false)
  const [laneModal, setLaneModal] = useState(false)
  const [currentRate, setCurrentRate] = useState<ShipperRateObjectAugmented | undefined>()
  const [currentRateId, setCurrentRateId] = useState<string | undefined>()
  const [showPresource, setShowPresource] = useState<boolean>(false)
  const { id: shipperRateId } = useParams<{
    id: string | undefined
  }>()
  const history = useHistory()

  const {
    filters,
    params: queryParams,
    setAllFilters: setFilters,
    setSort,
    sortField,
    sortOrder,
  } = useFilters(initialQueryParams)

  const {
    awardedLanes: rows,
    currentPage,
    handleFetchFirstPage,
    handlePageChange: onPageChange,
    loading,
    totalCount,
    totalPages: pageTotalCount,
  } = useAwardedLanes({ filters, sortField, sortOrder })

  const showPagination: boolean = pageTotalCount > 1

  useDeepCompareEffect(() => {
    handleFetchFirstPage()
  }, [queryParams.filters, queryParams.pageCount, sortField, sortOrder])

  // If an ID exists in the URL, open awarded lane modal
  useEffect(() => {
    if (shipperRateId) {
      setLaneModal(true)
      setCurrentRateId(shipperRateId)
    }
  }, [shipperRateId])

  const onRowClick = useCallback((shipperRate: ShipperRateObjectAugmented) => {
    setLaneModal(true)
    setCurrentRateId(String(shipperRate.id))
    history.push(`/awarded-lanes/${shipperRate.id}${history.location.search}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onOverflowClick = useCallback((shipperRate: ShipperRateObjectAugmented) => {
    setReassignModal(true)
    setCurrentRate(shipperRate)
  }, [])

  const clearFilters = () => {
    setFilters(initialQueryParams.filters)
  }

  const updateJob = () => {
    setLaneModal(false)
    history.push(`/awarded-lanes${history.location.search}`)
  }

  const onSortingChange = (field: string) => {
    setSort(field)
  }
  const openPresourceModal = () => setShowPresource(true)
  const closePresourceModal = () => setShowPresource(false)

  const closeReassignModalHandler = () => setReassignModal(false)

  return (
    <DocumentTitle title={generatePageTitle('Awarded Lanes')}>
      <>
        <Box p={4}>
          <Box mb={2} display='flex' justifyContent='space-between'>
            <Typography variant='h2'>Awarded Lanes</Typography>
            <div>
              <Button className='btn-secondary fr' onClick={openPresourceModal}>
                Presource Carriers
              </Button>
            </div>
          </Box>

          <Box mb={2}>
            <AwardedLanesFilters
              filters={filters}
              setFilters={setFilters}
              clearFilters={clearFilters}
            />
          </Box>
          <Box mb={2}>
            Displaying {rows.length} of {loading ? 0 : totalCount} Awarded Lanes
          </Box>
          <Box mb={2}>
            <AwardedLanesTable
              loading={loading}
              onSortingChange={onSortingChange}
              sortField={sortField as IShipperRatesSortByEnum}
              sortOrder={sortOrder as SortOrder}>
              {rows.length > 0 &&
                rows.map((shipperRate: ShipperRateObjectAugmented) => (
                  <AwardedLanesTableRow
                    key={shipperRate.id}
                    shipperRate={shipperRate}
                    onRowClick={onRowClick}
                    onOverflowClick={onOverflowClick}
                  />
                ))}
              {rows.length === 0 && !loading && <AwardedLanesEmpty />}
            </AwardedLanesTable>
          </Box>
          {showPagination && (
            <Box className='tc'>
              <Pagination
                currentPage={currentPage}
                numberOfPages={pageTotalCount}
                onPageChange={onPageChange}
                loading={loading}
                displayPageNumbers={false}
              />
            </Box>
          )}
        </Box>
        {laneModal && currentRateId && (
          <AwardedLaneModal awardedLaneId={currentRateId} onClose={updateJob} />
        )}
        {reassignModal && currentRate && (
          <ReassignModal
            currentRate={currentRate}
            onClose={closeReassignModalHandler}
            onSuccess={closeReassignModalHandler}
          />
        )}
        {showPresource && <PresourceModal onClose={closePresourceModal} />}
      </>
    </DocumentTitle>
  )
}

export default AwardedLanes
